import { ImageLinkFileSelectorComponent } from 'src/app/common/components/image.link.file.selector/image.link.file.selector.component';
import { ButtonTriggerEvent } from './../../../../common/components/button/button.component';
import { ImageSelectorComponent } from 'src/app/common/components/image.selector/image.selector.component';
import { finalize, switchMap } from 'rxjs/operators';
import { ImageService } from 'src/app/common/services/image/image.service';
import { VisitorEventService } from './../../services/visitor.event.service';
import { EventTimeService } from './../../services/event.time.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  Component,
  ViewEncapsulation,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  Inject,
  Optional,
  TemplateRef,
  OnDestroy,
  ElementRef,
  Renderer2,
  QueryList,
} from '@angular/core';
import { Observable, tap, catchError, of, takeUntil, EMPTY } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  IAppState,
  IFrontendSettings,
} from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { BaseComponent } from 'src/app/common/components/base.component';
import { select, Store } from '@ngrx/store';
import { frontendSettingsSelector } from 'src/app/modules/shared/ngrx.stores/frontend.settings/selectors';
import { ActivatedRoute } from '@angular/router';
import { CreateEventCommand } from 'src/app/modules/shared/models/create.event.command';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { PendoService } from 'src/app/modules/shared/services/pendo.service';
import { ChangeDetectorRef } from '@angular/core';
import { AddressInputComponent } from 'src/app/common/components/address.input/address.input.component';
import { RecurrenceRule } from 'src/app/modules/event-calendar/domain/recurrence/recurrence.rule';
import { OccurrenceService } from 'src/app/modules/event-calendar/services/occurrence.service';
import { EventFrequencyPickerComponent } from 'src/app/common/components/event.frequency.picker/event.frequency.picker.component';
import { RichTextBoxComponent } from 'src/app/common/components/rich.text.box/rich.text.box.component';
import { isRichHtmlEmpty, sanitizeRichHtml } from 'src/app/common/utils/html.utils';
import { SUN_EDITOR_CSS_URL } from 'src/app/common/constants/sun.editor';
import { guid } from 'src/app/common/utils/guid';


@Component({
  selector: 'ig-create-event',
  templateUrl: './create.event.component.html',
  styleUrls: ['./create.event.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class CreateEventComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  form: FormGroup;
  isCssReady = false;
  private _isSubmitting = false;
  private _clientRequestId: string;
  /** After create_in_progress, regenerate the request id if the user edits before retrying. */
  private _refreshRequestIdOnNextEdit = false;
  private _requestIdEditSub: { unsubscribe(): void } | null = null;
  hasSubmittedSuccessfully = false;
  pendingDuplicates: IPossibleDuplicateMatch[] = [];
  readonly submitSuccessMessage =
    'Thank you for submitting your event! Please allow at least 5 business days for review and approval.';

  @ViewChild('imgUploader', { static: false })
  imgUploader: ImageLinkFileSelectorComponent;

  @ViewChild(AddressInputComponent, { static: false })
  addressInput: AddressInputComponent;
  @ViewChild('frequencyPicker', { static: false })
  frequencyPicker: EventFrequencyPickerComponent;
  @ViewChild('richTextBox', { static: false })
  richTextBox: RichTextBoxComponent;
  @ViewChildren('submitSuccessBanner')
  submitSuccessBanners: QueryList<ElementRef<HTMLElement>>;
  private keyboardProtectionCleanups: (() => void)[] = [];

  constructor(
    public activatedRoute: ActivatedRoute,
    public eventService: VisitorEventService,
    public timeService: EventTimeService,
    public imgService: ImageService,
    public modalService: ModalService,
    @Optional() public dialogRef: MatDialogRef<CreateEventComponent>,
    private _fb: FormBuilder,
    private pendoService: PendoService,
    private _errorAlertService: ErrorAlertService,
    @Inject(MAT_DIALOG_DATA) @Optional() public data: ICreateEventModalData,
    private _store: Store<IAppState>,
    private cdRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private occurrenceService: OccurrenceService,
  ) {
    super({ activatedRoute });

    data = data || { isForHub: false };

    this.resetForm();

    // handle browser back button click, close the view and reload the page
    window.addEventListener('popstate', this.hashChanged.bind(this));

    // as this modal is ShadowDom, we need to inject the styles into it
    dialogRef?.afterOpened().subscribe(() => {
      modalService.injectStyle(
        '.cdk-overlay-container ig-create-event',
        [SUN_EDITOR_CSS_URL],
        () => { this.isCssReady = true; },
        this.cdRef,
        'CreateEventComponent',
      );
    });

    // DXP-2941: embedded page (no dialog) — delay until the shadow root is ready
    if (!dialogRef) {
      setTimeout(() => {
        modalService.injectStyle(
          'ig-create-event',
          [SUN_EDITOR_CSS_URL],
          () => { this.isCssReady = true; },
          this.cdRef,
          'CreateEventComponent-Embedded',
        );
      }, 200);
    }
  }

  ngAfterViewInit(): void {
    this.setupKeyboardProtectionForAllInputs();
  }

  override ngOnDestroy(): void {
    this.keyboardProtectionCleanups.forEach((cleanup) => cleanup());
    this.keyboardProtectionCleanups = [];
  }

  private setupKeyboardProtectionForAllInputs(): void {
    setTimeout(() => {
      const componentElement = this.elementRef.nativeElement;
      if (!componentElement) return;
      const findInputs = (
        root: Element | ShadowRoot,
      ): (HTMLInputElement | HTMLTextAreaElement)[] => {
        const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = [];

        const directInputs = root.querySelectorAll('input, textarea');
        directInputs.forEach((el) => {
          if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            inputs.push(el);
          }
        });

        root.querySelectorAll('*').forEach((el) => {
          if (el.shadowRoot) {
            inputs.push(...findInputs(el.shadowRoot));
          }
        });

        return inputs;
      };

      const allInputs = findInputs(componentElement);

      allInputs.forEach((input) => {
        if ((input as any).__imgoingKeyboardProtected) return;

        const handleKeyDownCapture = (e: KeyboardEvent) => {
          if (e.key === ' ' || e.code === 'Space') {
            e.stopPropagation();
          }
        };

        const handleKeyPressCapture = (e: KeyboardEvent) => {
          if (e.key === ' ' || e.code === 'Space') {
            e.stopPropagation();
          }
        };

        input.addEventListener('keydown', handleKeyDownCapture, true);
        input.addEventListener('keypress', handleKeyPressCapture, true);

        (input as any).__imgoingKeyboardProtected = true;
        this.keyboardProtectionCleanups.push(() => {
          input.removeEventListener('keydown', handleKeyDownCapture, true);
          input.removeEventListener('keypress', handleKeyPressCapture, true);
          delete (input as any).__imgoingKeyboardProtected;
        });
      });

      this.setupScrollProtection(componentElement);
    }, 300);
  }

  private setupScrollProtection(componentElement: Element): void {
    const isCherokeeClient =
      typeof document !== 'undefined' && /Cherokee/i.test(document.body.className);
    const WHEEL_LINE_HEIGHT_PX = 40;
    const scrollableContainers: HTMLElement[] = [];

    const findScrollable = (root: Element | ShadowRoot) => {
      const allElements = root.querySelectorAll('*');
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          const style = window.getComputedStyle(el);
          const overflowY = style.overflowY;
          const overflowX = style.overflowX;

          if (
            overflowY === 'auto' ||
            overflowY === 'scroll' ||
            overflowX === 'auto' ||
            overflowX === 'scroll'
          ) {
            scrollableContainers.push(el);
          }
        }

        if (el.shadowRoot) {
          findScrollable(el.shadowRoot);
        }
      });
    };

    findScrollable(componentElement);

    scrollableContainers.forEach((container) => {
      if ((container as any).__imgoingScrollProtected) return;

      const handleWheelCapture = (e: WheelEvent) => {
        const target = e.target as Node;
        if (container.contains(target) && componentElement.contains(target)) {
          const { scrollTop, scrollHeight, clientHeight } = container;
          const canScroll = scrollHeight > clientHeight;

          if (!canScroll) {
            return;
          }

          const isScrollingDown = e.deltaY > 0;
          const isAtTop = scrollTop <= 0;
          const isAtBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;

          if ((isAtTop && !isScrollingDown) || (isAtBottom && isScrollingDown)) {
            return;
          }

          e.preventDefault();
          e.stopPropagation();

          let delta = e.deltaY;
          if (isCherokeeClient) {
            if (e.deltaMode === 1) delta = e.deltaY * WHEEL_LINE_HEIGHT_PX;
            else if (e.deltaMode === 2) delta = e.deltaY * container.clientHeight;
          }
          container.scrollTop += delta;
        }
      };

      container.addEventListener('wheel', handleWheelCapture, { capture: true, passive: false });

      (container as any).__imgoingScrollProtected = true;
      this.keyboardProtectionCleanups.push(() => {
        container.removeEventListener('wheel', handleWheelCapture, true);
        delete (container as any).__imgoingScrollProtected;
      });
    });
  }

  get recurrenceControl(): FormControl {
    return this.form.get('recurrence') as FormControl;
  }

  public get isFormInValid() {
    return this.form.invalid || !this.imgUploader?.isValid;
  }


  onlineEventChange($event) {
    this.form.get('isOnlineEvent').setValue($event.checked);
    const addressControl = this.form.get('address');

    if ($event.checked) {
      addressControl?.clearValidators();
      addressControl?.updateValueAndValidity();
      this.form.get('address').setValue('');
      this.form.get('isHybridEvent').setValue(false);
    } else {
      addressControl?.setValidators([Validators.required, this.addressValidator.bind(this)]);
      addressControl?.updateValueAndValidity();
      this.form.get('address').setValue('');
    }
  }

  hybridChange($event) {
    this.form.get('isHybridEvent').setValue($event.checked);
    const addressControl = this.form.get('address');

    if ($event.checked) {
      this.form.get('isOnlineEvent').setValue(false);
      addressControl?.setValidators([Validators.required, this.addressValidator.bind(this)]);
      addressControl?.updateValueAndValidity();
      const address = this.form.get('address').value;
      if (address === 'Online Event') {
        this.form.get('address').setValue('');
      }
    } else {
      const isOnlineEvent = this.form.get('isOnlineEvent').value;
      if (!isOnlineEvent) {
        addressControl?.setValidators([Validators.required, this.addressValidator.bind(this)]);
        addressControl?.updateValueAndValidity();
      }
    }
  }

  setAsApprovedChange($event) {
    this.form.get('isApproved').setValue($event.checked);
  }

  close() {
    this.dialogRef.close();
  }

  imgSelected(imgFile: File) {
    this.form.get('imgFile').setValue(imgFile);
  }

  onDescriptionChange(content: string) {
    const control = this.form.get('description');
    control.setValue(content);
    control.markAsDirty();
  }

  private richTextRequiredValidator(control: FormControl) {
    return isRichHtmlEmpty(control.value) ? { required: true } : null;
  }

  private addressValidator(control: FormControl) {
    const value = control.value;

    if (!value) {
      return { required: true };
    }

    if (typeof value === 'string') {
      // User typed but did not select a place from the dropdown
      return { invalidAddress: true };
    }

    if (
      typeof value === 'object' &&
      (!value.address || (!value.address_components && !value.addressComponents))
    ) {
      return { invalidAddress: true };
    }

    return null;
  }

  processAddress() {
    const raw = this.form.get('address').value;
    if (!raw || typeof raw !== 'object') return raw;
    const address = { ...raw };
    if (address.address_components) {
      address.addressComponents = address.address_components;
      delete address.address_components;
    }
    this.form.get('address').setValue(address, { emitEvent: false });
    return address;
  }

  async submit(event: ButtonTriggerEvent, allowDuplicate = false) {
    if (this._isSubmitting) {
      event.immediateComplete();
      return;
    }
    if (this.richTextBox) {
      this.form.get('description').setValue(this.richTextBox.getEditorContent());
    }
    this.form.markAllAsTouched();

    const address = this.processAddress();
    const isOnline = this.form.get('isOnlineEvent').value;
    const addressControl = this.form.get('address');

    if (this.addressInput?.hasTextButNoSelection()) {
      addressControl.setErrors({ invalidAddress: true });
      addressControl.updateValueAndValidity();
    } else {
      addressControl.updateValueAndValidity();
    }

    if (!isOnline && addressControl.invalid) {
      this._errorAlertService.showErrorAlert(
        'The Location/Address field is invalid. Please type the street address and select the correct address from the drop-down.',
      );
      event.immediateComplete();
      return;
    }

    const rule: RecurrenceRule | null = this.form.get('recurrence')?.value ?? null;
    if (!rule || !rule.range?.startDateTime) {
      this._errorAlertService.showErrorAlert('Please set Start Date/Time for your event.');
      event.immediateComplete();
      return;
    }

    if (this.form.invalid || !this.imgUploader.isValid) {
      event.immediateComplete();
      return;
    }

    const description = sanitizeRichHtml(this.form.get('description').value);
    if (isRichHtmlEmpty(description)) {
      this._errorAlertService.showErrorAlert('Please provide a description for your event.');
      event.immediateComplete();
      return;
    }
    this.form.get('description').setValue(description);

    const eventTimes = this.occurrenceService.expand(rule);
    if (!eventTimes || eventTimes.length === 0) {
      this._errorAlertService.showErrorAlert(
        'Please add at least one event date with a valid start date and time.',
      );
      event.immediateComplete();
      return;
    }

    this.form.get('eventTimes').setValue(eventTimes);
    this.form
      .get('timezone')
      .setValue(
        this.timeService.getTZByFormatter(this.frontendSettings.customSettings.timezoneFormatter),
      );
    this.form.get('frequency').setValue(rule.kind);

    this._isSubmitting = true;
    const createdCommand = <CreateEventCommand>{ ...this.form.value };
    const eventName = this.form.get('name').value;
    this.imgUploader
      .uploadImg()
      .pipe(
        takeUntil(this.isDestroyed),
        switchMap((data) => {
          this.processAddress();
          this.form.get('imageLink').setValue(data.url);
          this.form.get('imgFile').setValue(null);
          if (this.data?.isForHub) {
            this.form.get('isHubCreated').setValue(true);
          }
          Object.assign(createdCommand, this.form.value);
          return this.eventService.createEvent(this.hubName, <CreateEventCommand>{
            ...this.form.value,
            clientRequestId: this._clientRequestId,
            checkDuplicates: true,
            ...(allowDuplicate ? { allowDuplicate: true } : {}),
          });
        }),
        tap(() => {
          this._refreshRequestIdOnNextEdit = false;
          this._requestIdEditSub?.unsubscribe();
          this._requestIdEditSub = null;
          this.pendingDuplicates = [];
          event.success();
          this.pendoService.track('Create Event - ' + eventName);
          // tell dashboard list to reload
          VisitorEventService.itemCreatedSubject$.next(createdCommand);
          if (!this.data?.isForHub) {
            this.hasSubmittedSuccessfully = true;
            this.scrollToSuccessState();
            return;
          }
          this.resetForm();
          if (this.data?.isForHub) {
            setTimeout(() => this.close(), 3000);
          }
        }),
        catchError((err: HttpErrorResponse) => {
          if (err?.status === 409 && err?.error?.error === 'possible_duplicate') {
            this.pendingDuplicates = Array.isArray(err.error.duplicates) ? err.error.duplicates : [];
            this.cdRef.markForCheck();
            return EMPTY;
          }
          if (err?.status === 409 && err?.error?.error === 'create_in_progress') {
            this.armRequestIdRefreshOnEdit();
            this._errorAlertService.showErrorAlert(
              err?.error?.message ||
                'Your event is still being saved. Please wait a moment and try again if it does not appear.',
            );
            return EMPTY;
          }
          const message = err?.error?.error || err?.error?.message || err?.message || 'Failed to save event. Please try again.';
          this._errorAlertService.showErrorAlert(message);
          return of(null);
        }),
        finalize(() => {
          this._isSubmitting = false;
          event.complete();
        }),
      )
      .subscribe();
  }

  onDismissDuplicateWarning(event?: ButtonTriggerEvent) {
    this.pendingDuplicates = [];
    event?.immediateComplete?.();
  }

  createDespiteDuplicates(event: ButtonTriggerEvent) {
    this.submit(event, true);
  }

  duplicateMatchLabel(match: IPossibleDuplicateMatch): string {
    const parts = [match.name || 'Existing event'];
    if (match.isApproved === false) parts.push('pending');
    else if (match.isApproved === true) parts.push('approved');
    if (match.startTime) {
      const date = new Date(match.startTime);
      if (!Number.isNaN(date.getTime())) parts.push(date.toLocaleString());
    }
    if (match.address) parts.push(match.address);
    return parts.join(' · ');
  }

  submitAnotherListing() {
    this.hasSubmittedSuccessfully = false;
    this.resetForm();
    setTimeout(() => {
      this.addressInput?.clear();
      this.frequencyPicker?.reset();
    }, 0);
    this.scrollFormToTop();
  }

  private armRequestIdRefreshOnEdit() {
    this._refreshRequestIdOnNextEdit = true;
    this._requestIdEditSub?.unsubscribe();
    this._requestIdEditSub = this.form.valueChanges.pipe(takeUntil(this.isDestroyed)).subscribe(() => {
      if (!this._refreshRequestIdOnNextEdit) return;
      this._refreshRequestIdOnNextEdit = false;
      this._clientRequestId = guid();
      this._requestIdEditSub?.unsubscribe();
      this._requestIdEditSub = null;
    });
  }

  private resetForm() {
    this._refreshRequestIdOnNextEdit = false;
    this._requestIdEditSub?.unsubscribe();
    this._requestIdEditSub = null;
    this._clientRequestId = guid();
    this.pendingDuplicates = [];
    this.form = this._fb.group({
      name: [null, [Validators.required]],
      description: [null, [this.richTextRequiredValidator.bind(this)]],
      category: [null, []],
      recurrence: [null, [Validators.required]],
      eventTimes: [],
      address: [null, [Validators.required, this.addressValidator.bind(this)]],
      host: [null, [Validators.required]],
      venue: [null, []],
      timezone: [null, []],
      hostLink: [null, []],
      eventLink: [null, []],
      organizerEmail: [null, []],
      ticketUri: [null, []],
      imageLink: [null, []],
      isHybridEvent: [false, []],
      isOnlineEvent: [false, []],
      imgFile: [null, []],
      isApproved: [false, []],
      isHubCreated: [false, []],
      frequency: ['custom', []],
    });

    this.imgUploader?.clear();
    this.richTextBox?.setEditorContent('');
  }

  private scrollToSuccessState() {
    setTimeout(() => {
      const banner = this.submitSuccessBanners?.last?.nativeElement;
      if (!banner) {
        this.scrollFormToTop();
        return;
      }
      banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  private scrollFormToTop() {
    const hostElement = this.elementRef?.nativeElement as HTMLElement;
    const scrollSection = hostElement?.querySelector('.form-scroll-section') as HTMLElement;
    scrollSection?.scrollTo({ top: 0, behavior: 'smooth' });
    hostElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * handle browser back button click, close the view
   * only close the page is not enough, it will always have other loading issues for the script embedding solution
   * so force reload the page for now
   * */
  // @HostListener('window:popstate', ['$event'])
  private hashChanged($event: Event) {
    $event.preventDefault();
    let url = window.location.href.split('imgoing-event')[0];
    url = url.endsWith('?') ? url.substring(0, url.length - 1) : url;
    window.location.href = url;
    this.close();
  }
}

export interface ICreateEventModalData {
  isForHub: boolean;
}

export interface IPossibleDuplicateMatch {
  eventId?: string;
  name?: string;
  startTime?: string | Date | null;
  address?: string;
  isApproved?: boolean;
  reasons?: string[];
}
